import { useEffect, useState } from "react";
import { apiGet, apiPost } from "../lib/api";
import { Card, Pill, Th, Td } from "../components/ui";

type Site = {
  id: string;
  name: string;
  address: string | null;
  is_active: number;
  go_live_date: string | null;
  alias_count: number;
};
type Unresolved = { name: string; dataset: string; first_seen: string; ingest_id: number | null; region: string | null };
type OpsArea = { id: string; name: string };

export default function SitesScreen() {
  const [sites, setSites] = useState<Site[]>([]);
  const [unresolved, setUnresolved] = useState<Unresolved[]>([]);
  const [opsAreas, setOpsAreas] = useState<OpsArea[]>([]);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [areaPicks, setAreaPicks] = useState<Record<string, string>>({});
  const [activePicks, setActivePicks] = useState<Record<string, boolean>>({});
  const [msg, setMsg] = useState<string | null>(null);

  function load() {
    apiGet<{ sites: Site[] }>("/api/admin/sites").then((r) => setSites(r.sites));
    apiGet<{ unresolved: Unresolved[] }>("/api/admin/unresolved").then((r) => setUnresolved(r.unresolved));
    apiGet<{ opsAreas: OpsArea[] }>("/api/admin/ops-areas").then((r) => setOpsAreas(r.opsAreas));
  }
  useEffect(load, []);

  /** The ops area to pre-pick: the one named like the Forecasts region, if any. */
  function areaFor(u: Unresolved): string {
    if (areaPicks[u.name] !== undefined) return areaPicks[u.name];
    return opsAreas.find((a) => a.name === u.region)?.id ?? "";
  }

  async function createSite(u: Unresolved) {
    const opsAreaId = areaFor(u);
    if (!opsAreaId) return;
    setMsg(null);
    try {
      const r = await apiPost<{ active: boolean }>("/api/admin/unresolved/create-site", {
        name: u.name, opsAreaId, active: !!activePicks[u.name],
      });
      setMsg(`Created "${u.name}" as a new site${r.active ? "" : " (not active in Shifts)"}.`);
      load();
    } catch (e: any) {
      setMsg(String(e.message ?? e));
    }
  }

  async function resolve(name: string) {
    const siteId = picks[name];
    if (!siteId) return;
    setMsg(null);
    try {
      await apiPost("/api/admin/unresolved/resolve", { name, siteId });
      setMsg(`Resolved "${name}".`);
      load();
    } catch (e: any) {
      setMsg(String(e.message ?? e));
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl text-ink">Sites</h1>
      {msg && <p className="text-sm text-neutral-500">{msg}</p>}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3.5 lg:grid-cols-[1.6fr_1fr]">
        <Card>
          <h2 className="mb-3 text-lg">All sites</h2>
          <div className="max-h-[560px] overflow-y-auto overflow-x-auto">
            <table className="w-full min-w-[420px]">
              <thead>
                <tr>
                  <Th>name</Th>
                  <Th>go-live</Th>
                  <Th>aliases</Th>
                  <Th>status</Th>
                </tr>
              </thead>
              <tbody>
                {sites.map((s) => (
                  <tr key={s.id}>
                    <Td>
                      {s.name}
                      <small className="block text-neutral-400">{s.address ?? s.id}</small>
                    </Td>
                    <Td>{s.go_live_date ?? "—"}</Td>
                    <Td right>{s.alias_count}</Td>
                    <Td>
                      <Pill tone={s.is_active ? "ok" : "grey"}>{s.is_active ? "active" : "inactive"}</Pill>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <h2 className="mb-3 text-lg">Unresolved names</h2>
          <p className="mb-3 text-xs text-neutral-400">
            A name a parser could not match to a site. Resolving it adds the alias and backfills existing rows.
          </p>
          <div className="flex max-h-[500px] flex-col gap-2.5 overflow-y-auto">
            {unresolved.map((u) => (
              <div key={`${u.name}|${u.dataset}`} className="rounded-lg border border-neutral-200 p-2.5">
                <div className="text-sm font-medium text-ink">{u.name}</div>
                <div className="text-xs text-neutral-400">{u.dataset}{u.region ? ` · ${u.region}'s region` : ""} · first seen {u.first_seen}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <select
                    value={picks[u.name] ?? ""}
                    onChange={(e) => setPicks((p) => ({ ...p, [u.name]: e.target.value }))}
                    className="min-h-[44px] min-w-0 flex-1 rounded-lg border border-neutral-300 px-2 py-1.5 text-sm outline-none focus:border-brand"
                  >
                    <option value="">choose a site…</option>
                    {sites.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => resolve(u.name)}
                    disabled={!picks[u.name]}
                    className="min-h-[44px] rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                  >
                    Resolve
                  </button>
                </div>
                {!u.name.startsWith("__") && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-2">
                    <span className="text-xs text-neutral-400">or create it as a new site in</span>
                    <select
                      value={areaFor(u)}
                      onChange={(e) => setAreaPicks((p) => ({ ...p, [u.name]: e.target.value }))}
                      className="min-h-[44px] min-w-0 flex-1 rounded-lg border border-neutral-300 px-2 py-1.5 text-sm outline-none focus:border-brand"
                    >
                      <option value="">choose an ops area…</option>
                      {opsAreas.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                    <label className="flex min-h-[44px] items-center gap-1.5 text-xs text-neutral-500">
                      <input
                        type="checkbox"
                        checked={!!activePicks[u.name]}
                        onChange={(e) => setActivePicks((p) => ({ ...p, [u.name]: e.target.checked }))}
                      />
                      active in Shifts
                    </label>
                    <button
                      onClick={() => createSite(u)}
                      disabled={!areaFor(u)}
                      className="min-h-[44px] rounded-lg border border-brand px-3 py-1.5 text-xs font-semibold text-brand disabled:opacity-40"
                    >
                      Create site
                    </button>
                  </div>
                )}
              </div>
            ))}
            {unresolved.length === 0 && <p className="text-sm text-neutral-400">Nothing unresolved.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
